import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
  Autocomplete,
} from "@mui/material";
import API from "../../utils/Axios.utils";
import { parseStatusLabel } from "../../utils/statusUtils";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Interview_Scheduled", label: "Interview Scheduled" },
  { value: "Interview_Cancelled", label: "Interview Cancelled" },
  { value: "Staffed", label: "Staffed" },
];

const STATUS_BADGE_MAP = {
  L1_Round: "status-badge badge-l1",
  L2_Round: "status-badge badge-l2",
  L3_Round: "status-badge badge-l3",
  Onboarded: "status-badge badge-offered",
  Onboarding_In_Process: "status-badge badge-onboarding",
  Awaiting_for_slots: "status-badge badge-awaiting",
};

const statusBadgeClass = (status) =>
  STATUS_BADGE_MAP[status] || "status-badge badge-default";

const JOB_STATUS_BADGE_MAP = {
  Active: "status-badge badge-job-active",
  "Partially-Fulfilled": "status-badge badge-job-partial",
  "Full-Filled": "status-badge badge-job-full",
  Critical: "status-badge badge-job-critical",
  Hold: "status-badge badge-job-hold",
  Cancelled: "status-badge badge-job-cancelled",
  "Close-No_Client_Response": "status-badge badge-job-closed",
  "Close-Unable_To_Fulfill": "status-badge badge-job-closed",
};

const jobStatusBadgeClass = (status) =>
  JOB_STATUS_BADGE_MAP[status] || "status-badge badge-default";

const formatStatusLabel = (status) =>
  status ? parseStatusLabel(status) : "—";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  } catch {
    return dateStr;
  }
};

const PAGE_SIZE = 10;

const EMPTY_FILTERS = { name: "", jobTitle: "", coreSkill: "", status: "" };

const AllCandidatesTab = () => {
  const [candidates, setCandidates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [coreSkills, setCoreSkills] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);

  const [draftName, setDraftName] = useState("");
  const [draftJobTitle, setDraftJobTitle] = useState("");
  const [draftCoreSkill, setDraftCoreSkill] = useState("");
  const [draftStatus, setDraftStatus] = useState("");

  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    API.get("/api/core-skill/coreSkills")
      .then((res) => setCoreSkills(res.data?.coreSkills || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    API.get("/api/job/all-job-title")
      .then((res) => setJobOptions(res.data?.jobs || []))
      .catch(() => {});
  }, []);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, pageSize: PAGE_SIZE });
      if (appliedFilters.name) params.set("search", appliedFilters.name);
      if (appliedFilters.jobTitle) params.set("job_code", appliedFilters.jobTitle);
      if (appliedFilters.coreSkill) params.set("core_skill_ids", appliedFilters.coreSkill);
      if (appliedFilters.status) params.set("status", appliedFilters.status);

      const res = await API.get(`/api/profile/all_candidate_profile?${params}`);
      const list = res?.data?.candidates || [];
      setCandidates(list);
      setTotalCount(res?.data?.totalCount || 0);
    } catch {
      setError("Failed to load candidates. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleSubmit = () => {
    setPage(1);
    setAppliedFilters({
      name: draftName,
      jobTitle: draftJobTitle,
      coreSkill: draftCoreSkill,
      status: draftStatus,
    });
  };

  const handleClear = () => {
    setDraftName("");
    setDraftJobTitle("");
    setDraftCoreSkill("");
    setDraftStatus("");
    setPage(1);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const showInterviewDate = Boolean(appliedFilters.status);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #E2E8F0", borderRadius: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Candidate Name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            sx={{ minWidth: 170 }}
            className="filter-input"
          />

          <Autocomplete
            size="small"
            sx={{ minWidth: 200 }}
            options={jobOptions}
            getOptionLabel={(j) => j.title || ""}
            isOptionEqualToValue={(a, b) => a.job_code === b.job_code}
            value={jobOptions.find((j) => j.job_code === draftJobTitle) || null}
            onChange={(_, val) => setDraftJobTitle(val?.job_code || "")}
            renderInput={(params) => <TextField {...params} label="Job Title" />}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Core Skill</InputLabel>
            <Select
              value={draftCoreSkill}
              label="Core Skill"
              onChange={(e) => setDraftCoreSkill(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {coreSkills.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={draftStatus}
              label="Status"
              onChange={(e) => setDraftStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disableElevation
            sx={{
              bgcolor: "#0369A1",
              "&:hover": { bgcolor: "#0284C7" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "12px",
              px: 3,
            }}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{
              borderColor: "#E2E8F0",
              color: "#64748B",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "12px",
              px: 3,
            }}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, overflow: "hidden" }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead className="reskinned-table-head">
              <TableRow>
                {[
                  "Candidate Name",
                  "Job Code",
                  "Job Title",
                  "Job Status",
                  "Core Skills",
                  "Status",
                  ...(!showInterviewDate ? ["Status Update Date"] : []),
                  ...(showInterviewDate ? ["Interview Date"] : []),
                  "Client",
                  "Supplier",
                ].map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 700, color: "#0369A1", py: 1.5 }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={showInterviewDate ? 10 : 9} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : candidates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showInterviewDate ? 10 : 9} align="center" sx={{ py: 5, color: "#94A3B8" }}>
                    No candidates found.
                  </TableCell>
                </TableRow>
              ) : (
                candidates.map((c) => (
                  <TableRow
                    key={c.profile_id}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{c.candidate_name}</TableCell>
                    <TableCell sx={{ color: "#64748B" }}>{c.job_code || "—"}</TableCell>
                    <TableCell>{c.job?.title || "—"}</TableCell>
                    <TableCell>
                      {c.job?.job_status ? (
                        <span className={jobStatusBadgeClass(c.job.job_status)}>
                          {formatStatusLabel(c.job.job_status)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {c.coreSkillName ? (
                        <Chip
                          label={c.coreSkillName}
                          size="small"
                          sx={{ bgcolor: "#F1F5F9", color: "#475569", fontWeight: 500 }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={statusBadgeClass(c.detailed_status)}>
                        {formatStatusLabel(c.detailed_status)}
                      </span>
                    </TableCell>
                    {!showInterviewDate && (
                      <TableCell sx={{ color: "#64748B", whiteSpace: "nowrap" }}>
                        {formatDate(c.status_date)}
                      </TableCell>
                    )}
                    {showInterviewDate && (
                      <TableCell sx={{ color: "#64748B", whiteSpace: "nowrap" }}>
                        {c.interview_datetime || "—"}
                      </TableCell>
                    )}
                    <TableCell>{c.client?.client_name || "—"}</TableCell>
                    <TableCell>{c.supplier?.supplier_org_name || "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
              borderTop: "1px solid #E2E8F0",
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AllCandidatesTab;
